import { useMemo } from 'react';

import { useQueries } from '@tanstack/react-query';

import { contactsAPI, contactsEndpoints } from '@/api/contacts';
import type {
  ConsentChannelType,
  ConsentStatus,
  ContactIdentity,
  IdentityConsent,
} from '@/shared/types/contacts';

/** The orchestrator returns consents as `{ rows }`; tolerate a bare array too. */
const toConsentRows = (data: unknown): IdentityConsent[] => {
  if (Array.isArray(data)) {
    return data as IdentityConsent[];
  }
  const rows = (data as { rows?: unknown } | undefined)?.rows;
  if (Array.isArray(rows)) {
    return rows as IdentityConsent[];
  }
  const consents = (data as { consents?: unknown } | undefined)?.consents;
  return Array.isArray(consents) ? (consents as IdentityConsent[]) : [];
};

/**
 * Every transport a consent row can be scoped to. A row with `channel_type: null`
 * is a wildcard that covers all of them.
 */
const CONSENT_TRANSPORTS: ConsentChannelType[] = ['sms', 'voice', 'whatsapp', 'rcs', 'email'];

export interface IdentityConsentResolution {
  hasGranted: boolean;
  hasRevoked: boolean;
}

/**
 * Resolve one identity's rows into "is any transport granted / revoked", following the
 * orchestrator's most-specific-wins precedence: a channel-scoped row overrides the
 * wildcard (`channel_type: null`) row, so `wildcard: granted` + `whatsapp: revoked`
 * resolves to revoked on WhatsApp and granted elsewhere.
 *
 * Transports with no row at all are deliberately ignored — they carry no explicit
 * consent (the orchestrator treats that as an implicit grant) and must not be
 * reported as either granted or revoked.
 */
export const resolveIdentityConsent = (rows: IdentityConsent[]): IdentityConsentResolution => {
  const wildcardStatus = rows.find((row) => row.channel_type === null)?.status;
  const byTransport = new Map<ConsentChannelType, ConsentStatus>();
  rows.forEach((row) => {
    if (row.channel_type) {
      byTransport.set(row.channel_type, row.status);
    }
  });

  let hasGranted = false;
  let hasRevoked = false;
  // Union of the transports we know about and any the server has since added —
  // `channel_type` has no DB constraint precisely so a new transport needs no
  // migration, and a scoped opt-out on one must not be dropped here.
  const transports = new Set<ConsentChannelType>([...CONSENT_TRANSPORTS, ...byTransport.keys()]);
  transports.forEach((transport) => {
    const status = byTransport.get(transport) ?? wildcardStatus;
    if (status === 'granted') {
      hasGranted = true;
    }
    if (status === 'revoked') {
      hasRevoked = true;
    }
  });

  return { hasGranted, hasRevoked };
};

/**
 * Sidebar helper: the single "Consent status" value for a contact, aggregated over
 * every reachable (email/phone, not soft-deleted) identity.
 *
 * Aggregation is revoked-wins: one recorded opt-out on any transport of any identity
 * shows as revoked. A one-line summary cannot name the channel it applies to, and
 * understating reachability is far safer than hiding an opt-out behind "Granted".
 *
 * Returns `undefined` when nothing can be asserted — no explicit consent recorded, or
 * a query that is still in flight or failed. The row is hidden in that case rather
 * than guessing.
 */
export const useContactConsentStatus = (contactId?: string, identities: ContactIdentity[] = []) => {
  const reachableIdentities = useMemo(
    () =>
      identities.filter(
        (identity) =>
          (identity.type === 'email' || identity.type === 'phone') && !identity.deleted_at
      ),
    [identities]
  );

  const results = useQueries({
    queries: reachableIdentities.map((identity) => ({
      queryKey: [contactsEndpoints.consents(contactId ?? '', identity.identity_id)],
      queryFn: () => contactsAPI.listConsents(contactId!, identity.identity_id),
      enabled: !!contactId,
    })),
  });

  const isPending = results.some((result) => result.isPending);
  const hasError = results.some((result) => result.isError);

  // Only successful queries may be read. A failed request must not collapse into
  // "no revoked row" and present an opted-out contact as granted.
  const resolutions = results
    .filter((result) => result.isSuccess)
    .map((result) => resolveIdentityConsent(toConsentRows(result.data)));

  const hasRevoked = resolutions.some((resolution) => resolution.hasRevoked);
  const hasGranted = resolutions.some((resolution) => resolution.hasGranted);

  const getConsentStatus = (): ConsentStatus | undefined => {
    // A revocation we already know about is a fact worth showing even if a sibling
    // identity is still loading or errored.
    if (hasRevoked) {
      return 'revoked';
    }
    if (isPending || hasError) {
      return undefined;
    }
    return hasGranted ? 'granted' : undefined;
  };

  const getStatus = () => {
    if (isPending) {
      return 'pending' as const;
    }
    if (hasError) {
      return 'error' as const;
    }
    return 'success' as const;
  };

  return { consentStatus: getConsentStatus(), status: getStatus() };
};
